import { Link, graphql, withPrefix } from "gatsby"
import RichText from "@components/rich-text/renderer"
import { Head as BaseHead } from "@components/Head/Head"
import { arrowLeft } from "@components/button.module.css"
import {
  section,
  head,
  video as video_cn,
  details,
  detail1,
  detail2,
  detail3,
  speaker as speaker_cn,
  speakerImg,
  speakerContent,
  description as description_cn,
  browseStorie,
  transcript as transcript_cn,
  share,
  socialMedia,
} from "./altitude-video-page.module.css"
import { Button as ConsistentlyButton } from "@fastly/consistently"
import { FastlyImage } from "@components/FastlyImage"
import { reduceMicrocopySet } from "@utils/reduceMicrocopy"
import { MikanSpan } from "@components/Mikan"
import useSocialMedia from "@utils/useSocialMedia"

const AltitudeVideoPage = ({ data: { video, microcopySet } }) => {
  const { description, presentationType, speakers, title, transcript, url, duration, cta } = video
  const socialMediaLinks = useSocialMedia()

  const vimeoId = url.url.match(/[0-9]+/)?.[0]
  const isYoutube = url.url.match(/youtube\.com\/embed/)

  const labels = reduceMicrocopySet(microcopySet)

  const socialMediasUrl = [
    {
      logo: "/g-static/altitude/2020/twitter_black.svg",
      link: socialMediaLinks.twitter,
      name: "twitter",
    },
    {
      logo: "/g-static/altitude/2020/linkedIN_black.svg",
      link: socialMediaLinks.linkedin,
      name: "linkedin",
    },
    {
      logo: "/g-static/altitude/2020/facebook_black.svg",
      link: socialMediaLinks.facebook,
      name: "facebook",
    },
  ]
  const socialMedias = (
    <>
      {socialMediasUrl.map((socialMedia) => (
        <Link href={socialMedia.link} key={socialMedia.name} target="_blank" rel="noopener noreferrer">
          <img src={socialMedia.logo} alt={socialMedia.name} />
        </Link>
      ))}
    </>
  )

  const ctaParsed = JSON.parse(cta.raw)
  const buttonTitle = cta && ctaParsed.content[0].content[0].value
  const buttonSubTitle = cta && ctaParsed.content[1].content[0].value
  const buttonCta = cta && `${cta.references[0].buttonText}`
  const buttonUrl = cta && `${cta.references[0].url}`

  return (
    <>
      <div className="page__content">
        <section className={section}>
          <div className={head}>
            <Link to="/altitude/2020" className={arrowLeft}>
              <MikanSpan text={labels.viewallaltitude2020talks} />
            </Link>
            <div>
              <FastlyImage src="/g-static/altitude/2020/altitude-horizontal.svg" alt="altitude logo" />
            </div>
          </div>

          <div className={video_cn}>
            <div className="embed-responsive embed-responsive-16by9">
              {vimeoId ? (
                <iframe
                  title={title}
                  id="vimeo_player"
                  src={`https://player.vimeo.com/video/${vimeoId}`}
                  className="embed-responsive-item"
                  webkitallowfullscreen=""
                  mozallowfullscreen=""
                  allowFullScreen=""
                  data-ready="true"
                  data-vimeo-tracked="true"
                />
              ) : null}
              {isYoutube ? (
                <iframe
                  title={title}
                  id="ytplayer"
                  type="text/html"
                  className="embed-responsive-item"
                  src={url.url}
                  frameBorder="0"
                  allowFullScreen
                />
              ) : null}
            </div>
          </div>
          <div className={details}>
            <div className={detail1}>
              <div>
                <MikanSpan text={labels[presentationType]} /> {duration && <span>{duration}</span>}
              </div>
              <h1>
                <MikanSpan text={title} />
              </h1>
            </div>
            <div className={detail2}>
              <div>
                <MikanSpan text={labels.presentedby} />
              </div>
              <div>
                {speakers.map((speaker) => (
                  <div className={speaker_cn} key={speaker.name}>
                    <div className={speakerImg}>
                      <FastlyImage
                        src={speaker?.photo?.file?.url}
                        params="?width=24&height=24%&fit=crop"
                        alt="speaker avatar"
                      />
                    </div>
                    <div className={speakerContent}>
                      <p>{speaker?.name}</p>
                      <p>{`${speaker?.title}${speaker?.company ? `, ${speaker?.company?.name}` : ""}`}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className={detail3}>{socialMedias}</div>
          </div>

          <hr></hr>

          <div className={description_cn}>
            <p>
              <MikanSpan text={description.description} />
            </p>
          </div>

          <div className={browseStorie}>
            <p>
              <MikanSpan text={buttonTitle} />
            </p>
            <p>
              <MikanSpan text={buttonSubTitle} />
            </p>
            <div>
              <ConsistentlyButton variant="main" url={withPrefix(buttonUrl)} icon="arrow">
                <MikanSpan text={buttonCta} />
              </ConsistentlyButton>
            </div>
          </div>

          {transcript && (
            <div className={transcript_cn}>
              <h2>
                <MikanSpan text={labels.videotranscript} />
              </h2>
              {<RichText body={transcript} />}
            </div>
          )}

          <hr></hr>

          <div className={share}>
            <Link to="/altitude/2020" className={arrowLeft}>
              <MikanSpan text={labels.viewallaltitude2020talks} />
            </Link>
            <div className={socialMedia}>
              <div>
                <MikanSpan text={labels.sharethistalk} />
              </div>
              <div>{socialMedias}</div>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default AltitudeVideoPage

export const query = graphql`
  query ($contentful_id: String!) {
    video: contentfulVideo(contentful_id: { eq: $contentful_id }) {
      contentful_id
      cta {
        raw
        references {
          buttonText
          url
        }
      }
      title
      duration
      url {
        url
      }
      metaTitle
      metaDescription
      description {
        description
      }
      industry
      keywords
      presentationType
      region
      slug
      speakers {
        name
        title
        photo {
          file {
            url
          }
        }
        company {
          name
        }
      }
      transcript {
        raw
      }
    }
    microcopySet: contentfulMicrocopySet(contentful_id: { eq: "1qP4gZPCE1BzdvN0zIoZWI" }, items: {}) {
      id
      items {
        text
        title
      }
    }
  }
`

export const Head = (props) => {
  const { data } = props
  data.page = {}
  data.page.socialTitle = data.video.metaTitle || data.video.title
  data.page.socialDescription = data.video.metaDescription || data.video.description

  return <BaseHead {...props} />
}
